/**
 * CostMeter — daily LLM spend pill rendered in the Topbar.
 *
 * Admin-only. Calls ``GET /api/v1/admin/costs`` once per minute and
 * shows a tiny progress bar against the per-day budget.
 *
 * Admin gate (history): an earlier version of this component
 * probed the endpoint blindly on mount and learned admin status
 * from the 403 response. That pattern spammed the backend logs
 * (one 403 per dashboard load — two in dev because Strict Mode
 * fires the effect twice). It also surfaced a misleading
 * "Forbidden" error in the browser console for every non-admin
 * user, including yourself if your uid isn't in
 * ``OMNI_ADMIN_USER_IDS``.
 *
 * Now we consume ``auth.isAdmin`` (derived from the session
 * token's ``role`` claim — same source the backend's
 * ``_require_admin`` consults). Non-admins render nothing and
 * make zero network calls. Admin status updates within one token
 * rotation if a role is granted.
 */
import * as React from "react";
import { useApiClient } from "@/api/useApiClient";
import { useAuth } from "@/auth/useAuth";
import { adminCosts, type CostSummaryResponse } from "@/api";

export function CostMeter() {
  const client = useApiClient();
  const { isAdmin } = useAuth();
  const [data, setData] = React.useState<CostSummaryResponse | null>(null);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    // Only admins fetch. ``isAdmin === null`` means the token
    // result hasn't resolved yet — wait silently rather than
    // probing and getting a 403.
    if (isAdmin !== true) return;

    let cancelled = false;
    const fetchCost = async () => {
      try {
        const { data: result } = await adminCosts({ client, query: { limit: 10 }, throwOnError: true });
        if (!cancelled) {
          setData(result);
          setError(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("Failed to fetch costs for Topbar", err);
          setError(true);
        }
      }
    };
    void fetchCost();
    const interval = setInterval(() => void fetchCost(), 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [client, isAdmin]);

  // Render nothing for non-admins (or while admin status is
  // still resolving) and for transient errors.
  if (isAdmin !== true || error || !data) {
    return null;
  }

  const dailyBudget = 10.0;
  const currentCost = data.total_usd || 0.0;
  const percentage = Math.min((currentCost / dailyBudget) * 100, 100);

  // Group by feature details for tooltip
  const breakdown = Object.entries(data.by_feature || {}).map(([feature, cost]) => (
    <div key={feature} className="flex justify-between gap-4 text-[10px]">
      <span className="capitalize">{feature}:</span>
      <span>${cost.toFixed(3)}</span>
    </div>
  ));

  return (
    <div className="relative group hidden md:flex items-center gap-2">
      <div className="flex flex-col items-end">
        <span className="text-[10px] font-mono text-fg-muted">
          ${currentCost.toFixed(2)} today
        </span>
        <div className="w-16 h-1.5 bg-bg-sunken border border-border overflow-hidden">
          <div
            className="h-full bg-accent transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Tooltip */}
      <div className="absolute top-8 right-0 hidden group-hover:block bg-bg-elevated border border-border shadow-lg p-2.5 z-50 w-44 font-mono text-xs om-fade-in">
        <div className="font-semibold border-b border-border pb-1 mb-1 text-[11px] text-fg">
          Cost Breakdown
        </div>
        {breakdown.length > 0 ? breakdown : <div className="text-[10px] text-fg-faint">No details</div>}
        <div className="border-t border-border mt-1.5 pt-1 flex justify-between font-semibold text-[10px]">
          <span>Total:</span>
          <span>${currentCost.toFixed(3)}</span>
        </div>
        <div className="flex justify-between text-[9px] text-fg-faint mt-0.5">
          <span>Limit:</span>
          <span>${dailyBudget.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}
